import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";

// Bilingual copy dictionary for the Mosquito Check wizard.
// Per-habitat labels/descriptions/solutions live in the shared pure module
// ./habitatCopy (used by both the resident walk and the sync staff card);
// everything else lives here.
const COPY = {
	app_name: { en: "Mosquito Check", es: "Revisión de mosquitos" },
	lang_toggle: { en: "Language", es: "Idioma" },
	step_of: { en: "Step {n} of {total}", es: "Paso {n} de {total}" },
	loading: { en: "Loading your inspection", es: "Cargando su inspección" },

	// Screen 1 — Welcome
	welcome_title: {
		en: "Welcome to {district_name}'s Mosquito Check",
		es: "Bienvenido al Mosquito Check de {district_name}",
	},
	welcome_body: {
		en: "A quick, guided walk to find mosquito breeding sources around your home. We'll show you what to look for, how to fix it, and when we can help. Takes about 5 minutes.",
		es: "Una caminata rápida y guiada para encontrar fuentes de mosquitos alrededor de su casa. Le mostraremos qué buscar, cómo corregirlo y cuándo podemos ayudarle. Toma unos 5 minutos.",
	},
	resume_note: {
		en: "You've already started — your answers are saved. Continue where you left off.",
		es: "Ya comenzó — sus respuestas están guardadas. Continúe donde lo dejó.",
	},
	start: { en: "Start", es: "Comenzar" },

	// Screen 2 — Guided Walk
	walk_title: { en: "Guided Walk", es: "Recorrido guiado" },
	walk_instructions: {
		en: "We'll show you the most common mosquito breeding sources one at a time. Walk your property and check each one as you go — tap I have this if you see something like the picture, or I don't have this if you don't. Be honest — a clean property is good news and may not need a visit.",
		es: "Le mostraremos las fuentes de mosquitos más comunes una por una. Recorra su propiedad y revise cada una a medida que avanza — toque Lo tengo si ve algo como la imagen, o No lo tengo si no lo ve. Sea honesto — una propiedad limpia es una buena noticia y quizá no necesite visita.",
	},
	counts: {
		en: "Confirmed: {confirmed} · Clear: {clear}",
		es: "Confirmados: {confirmed} · Sin presencia: {clear}",
	},
	step_progress: {
		en: "{remaining} left to check",
		es: "{remaining} por revisar",
	},
	view_all: { en: "View all", es: "Ver todo" },
	guided: { en: "One at a time", es: "Una por una" },
	done_title: {
		en: "You've checked everything",
		es: "Ha revisado todo",
	},
	done_body: {
		en: "All breeding sources have been checked. Continue when you're ready.",
		es: "Todas las fuentes de mosquitos han sido revisadas. Continúe cuando esté listo.",
	},
	review: { en: "Review answers", es: "Revisar respuestas" },
	back: { en: "Back", es: "Atrás" },
	continue: { en: "Continue", es: "Continuar" },

	// Habitat cards
	i_have_this: { en: "I have this", es: "Lo tengo" },
	i_dont_have_this: { en: "I don't have this", es: "No lo tengo" },
	remove: { en: "Remove", es: "Quitar" },
	undo: { en: "Undo", es: "Deshacer" },
	larvae_present: { en: "Larvae present?", es: "¿Hay larvas?" },
	yes: { en: "Yes", es: "Sí" },
	no: { en: "No", es: "No" },
	not_sure: { en: "Not sure", es: "No estoy seguro" },
	larvae_video_hint: {
		en: "Not sure? Watch a short video of mosquito larvae and pupae to help you decide.",
		es: "¿No está seguro? Vea un video breve de larvas y pupas de mosquito para ayudarle a decidir.",
	},
	larvae_video_btn: { en: "Watch video", es: "Ver video" },
	larvae_video_title: {
		en: "Larvae & pupae — what to look for",
		es: "Larvas y pupas — qué buscar",
	},
	larvae_video_attribution: {
		en: "Source: CDC. Use of this material does not imply endorsement by CDC or the U.S. Department of Health and Human Services. The original is available free at cdc.gov.",
		es: "Fuente: CDC. El uso de este material no implica el respaldo de los CDC ni del Departamento de Salud y Servicios Humanos de los EE. UU. El original está disponible gratuitamente en cdc.gov.",
	},
	close: { en: "Close", es: "Cerrar" },
	already_fixed: { en: "I already fixed it", es: "Ya lo corregí" },
	marked_not_present: {
		en: "Marked as not present",
		es: "Marcado como no presente",
	},
	solution_label: { en: "Solution", es: "Solución" },
	photo_alt: { en: "Photo of the habitat", es: "Foto del criadero" },
	remove_photo: { en: "Remove photo", es: "Quitar foto" },
	photo_saved: { en: "Saved photo", es: "Foto guardada" },

	// Screen 3 — Beyond Your Property
	beyond_title: {
		en: "Beyond Your Property",
		es: "Más allá de su propiedad",
	},
	beyond_body: {
		en: "Sometimes the problem is not in your yard. Tell us about mosquito sources you see nearby. {district_name} responds to all reports — you do not need to confront a neighbor or enter public property.",
		es: "A veces el problema no está en su jardín. Cuéntenos sobre fuentes de mosquitos que vea cerca. {district_name} responde a todos los reportes — no necesita confrontar a un vecino ni ingresar a propiedad pública.",
	},
	block_neighbor: { en: "Neighbor", es: "Vecino" },
	block_public: { en: "Public space", es: "Espacio público" },
	block_agricultural: { en: "Agricultural", es: "Agrícola" },
	yes_report: { en: "Yes, report this", es: "Sí, reportar esto" },
	describe_location: {
		en: "Describe the location and what you see",
		es: "Describa la ubicación y lo que ve",
	},
	add_photo: { en: "Add a photo", es: "Agregar una foto" },

	// Screen 4 — Contact & Location
	contact_title: { en: "Contact & Location", es: "Contacto y ubicación" },
	contact_body: {
		en: "Tell us who you are and where the property is located. We use this only to schedule and route your inspection — and only if you reported something that needs follow-up.",
		es: "Díganos quién es y dónde se encuentra la propiedad. Lo usamos únicamente para programar y dirigir su inspección — y solo si reportó algo que necesita seguimiento.",
	},
	your_name: { en: "Your name", es: "Su nombre" },
	phone_number: { en: "Phone number", es: "Número de teléfono" },
	email_optional: {
		en: "Email address (optional)",
		es: "Correo electrónico (opcional)",
	},
	property_address: {
		en: "Property address",
		es: "Dirección de la propiedad",
	},
	use_location: {
		en: "Use my current location",
		es: "Usar mi ubicación actual",
	},
	location_captured: {
		en: "Location captured",
		es: "Ubicación capturada",
	},
	submit: { en: "Submit", es: "Enviar" },

	// Screen 5 — Thank You
	thank_you_title: { en: "Thank you", es: "Gracias" },
	thank_you_body: {
		en: "Thank you for completing the Mosquito Check. Your answers will be reviewed to decide if an in-person visit is needed — if you reported something you couldn't fix, a technician may follow up with you. No need to call us: your report helps us find the true source of mosquitoes in your area. The best thing you can do is repeat this check once a week.",
		es: "Gracias por completar el Mosquito Check. Sus respuestas serán revisadas para decidir si se necesita una visita en persona — si reportó algo que no pudo arreglar, un técnico puede comunicarse con usted. No necesita llamarnos: su reporte nos ayuda a encontrar la verdadera fuente de mosquitos en su área. Lo mejor que puede hacer es repetir esta revisión una vez por semana.",
	},
	check_again: {
		en: "Do this check again",
		es: "Hacer esta revisión otra vez",
	},
	call_urgent: {
		en: "For urgent concerns, call",
		es: "Para preocupaciones urgentes, llame al",
	},

	// Errors / feedback
	err_save: {
		en: "We couldn't save your answer. Please try again.",
		es: "No pudimos guardar su respuesta. Intente de nuevo.",
	},
	err_photo: {
		en: "We couldn't upload your photo. Please try again.",
		es: "No pudimos subir su foto. Intente de nuevo.",
	},
	err_load: {
		en: "We couldn't load your inspection. Please try again.",
		es: "No pudimos cargar su inspección. Intente de nuevo.",
	},
	not_found_title: {
		en: "Inspection not found",
		es: "Inspección no encontrada",
	},
	err_not_found: {
		en: "This inspection link is not valid. Please contact {district_name} at {district_phone}.",
		es: "Este enlace de inspección no es válido. Comuníquese con {district_name} al {district_phone}.",
	},
	err_location_denied: {
		en: "Location access was denied. You can type the property address instead.",
		es: "Se denegó el acceso a la ubicación. Puede escribir la dirección de la propiedad.",
	},
	err_location_unavailable: {
		en: "Location is unavailable on this device.",
		es: "La ubicación no está disponible en este dispositivo.",
	},
	err_address_required: {
		en: "Please enter the property address so a technician can schedule follow-up.",
		es: "Ingrese la dirección de la propiedad para que un técnico pueda programar el seguimiento.",
	},
} as const;

// Identification clip for the "Not sure" larvae answer. Self-hosted from the
// RMO bundle (vite/rmo/public/media/mosquito) so it plays with no third party
// involved. Two encodings of the same clip; the <video> element picks one.
//
// Footage: CDC Aedes aegypti life-cycle b-roll — a US Government work in the
// public domain (17 U.S.C. §105). Three of its shots are joined here, left in
// their original order in the b-roll and each trimmed only: the larvae
// close-up, the pupae close-up, then the sandbox toys that hold water (cropped
// in, to show how little water it takes). Nothing inside a shot is altered,
// added, or reordered. The larvae shot is the softest of the three — it is
// there because it is the only close-up of the larval stage, which is what the
// resident is being asked to identify. CDC's terms make the attribution copy
// below part of the licence, not decoration.
export const LARVAE_VIDEO_URL = "/media/mosquito/larvae-clip.mp4";
export const LARVAE_VIDEO_WEBM_URL = "/media/mosquito/larvae-clip.webm";
export const LARVAE_VIDEO_POSTER_URL = "/media/mosquito/larvae-clip-poster.jpg";

export type CopyKey = keyof typeof COPY;

// Returns a translation function bound to the current store language.
// Reading `store.language` during render registers the reactivity
// dependency, so every label re-renders instantly on toggle.
export function useT() {
	const store = useStoreVirtualInspection();
	return (key: CopyKey, vars?: Record<string, string | number>): string => {
		const entry = COPY[key];
		const text = store.language === "es" ? entry.es : entry.en;
		if (!vars) {
			return text;
		}
		return text.replace(/\{(\w+)\}/g, (match, name: string) =>
			name in vars ? String(vars[name]) : match,
		);
	};
}

// Org identity is runtime data from the resident state payload
// (VirtualInspectionState.org_name / phone_office), never hard-coded copy.
// Call inside computed/render so the store read registers reactivity.
//
// Missing-field behavior (safe default, flagged for operator sign-off):
//   - name: neutral per-language fallback keeps sentences grammatical.
//   - phone: substitutes to "" — real orgs populate phone_office, so the
//     token sentences ("Call {district_phone}.", "...at {district_phone}.")
//     only render a dangling clause in impossible states. Use org.phone to
//     gate phone-dependent UI (e.g. the Thank You tel: block) instead.
export function useOrg(): { name: string; phone: string } {
	const store = useStoreVirtualInspection();
	const name = store.state?.org_name.trim();
	return {
		name:
			name ||
			(store.language === "es"
				? "su distrito de control de mosquitos"
				: "your mosquito control district"),
		phone: store.state?.phone_office.trim() || "",
	};
}
